import { MoneyAddIcon } from "@xola/icons";
import { almostZero } from "@xola/ui-kit";
import { isArray } from "lodash";
import React from "react";
import { Audit, LineItemType, Purchase, PurchaseItem } from "types";
import { Currency } from "../../../Common/Currency";
import { TimelineItem, TimelineItemReason } from "../TimelineItem";

interface RemoveChargeTimelineItemProps {
    purchase: Purchase;
    item: PurchaseItem;
    audit: Audit;
    showDate?: boolean;
}

export const RemoveChargeTimelineItem = ({
    purchase,
    item,
    audit,
    showDate = false,
}: RemoveChargeTimelineItemProps) => {
    const [oldAmount, , reason] = getAmount(item, audit);

    if (!RemoveChargeTimelineItem.supportsAudit(purchase, item, audit)) {
        return null;
    }

    return (
        <TimelineItem
            title="Remove Charge"
            audit={audit}
            icon={<MoneyAddIcon color="text-danger" />}
            color="danger"
            showDate={showDate}
            purchase={purchase}
        >
            <div className="space-y-2">
                <div className="flex">
                    <Currency shouldRemoveTrailingZeroes={false}>{oldAmount}</Currency>
                </div>
                <TimelineItemReason>{reason}</TimelineItemReason>
            </div>
        </TimelineItem>
    );
};

RemoveChargeTimelineItem.supportsAudit = (_purchase: Purchase, item: PurchaseItem, audit: Audit) => {
    const [oldAmount, newAmount] = getAmount(item, audit);
    return !almostZero(oldAmount) && !newAmount;
};

const getAmount = (item: PurchaseItem, audit: Audit): [number, number?, string?] => {
    if (!audit.data?.items) {
        return [0];
    }

    const auditItem =
        isArray(audit.data?.items) &&
        audit.data.items.find((auditItem: any) => auditItem.id === item?.id && auditItem.amount);
    if (!auditItem) {
        return [0];
    }

    const values: any = Object.keys(auditItem.lineItems ?? {});

    for (const value of values) {
        const isValueAdjustment =
            (auditItem.lineItems[value]?.old?.type === LineItemType.ADJUSTMENT && !auditItem.lineItems[value]?.new) ||
            (auditItem.lineItems[value]?.old?.type === LineItemType.TAXED_ADJUSTMENT &&
                !auditItem.lineItems[value]?.new);

        if (isValueAdjustment) {
            return [
                auditItem.lineItems[value]?.old?.amount,
                auditItem.lineItems[value]?.new?.amount,
                auditItem.lineItems[value]?.old?.name,
            ];
        }
    }

    return [0];
};
